import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "react-query";
import { debounce } from "lodash";
import { SearchField, Span, UiIcon, Spinner } from "@icg360/design-system";
import { externalResetChildForm } from "@icg360/rex";
import PropTypes from "prop-types";

import { GetMortgageeProperties } from "../../utils/get-mortgagee-properties";
import { hasFeatureFlag } from "../../store/selectors";
import { QTC, MORTGAGEE_CHANGE } from "../../constants";
import { toggleMortgageeModal } from "../../store/quote-transaction-component";
import { track } from "../../units/tracking";

const INPUT_SOURCES = MORTGAGEE_CHANGE.INPUT_SOURCES;

import {
  StyledSearchField,
  DropdownMenu,
  DropdownSuggestion,
  MortgageeName,
  DropdownAction,
  SearchError,
  StyledSpinner
} from "./styles";

export interface Mortgagee {
  [key: string]: string;
}

interface MortgageeSearchResult {
  error: string;
  matches?: number;
  mortgagees: Mortgagee[];
}

interface MortgageeSearch {
  onChange: (mortgagee: Mortgagee, searchText?: string) => void;
  fusionPosition?: number;
  onEnterMortgagee?: () => void;
  onBlur?: () => void;
}

export const MortgageeSearch = ({
  onChange,
  fusionPosition,
  onEnterMortgagee,
  onBlur
}: MortgageeSearch) => {
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const MORTGAGEES_LIST_LIMIT = 10;
  const isQTC = !!fusionPosition;

  const hasUpdatedMortgageesResponse = useSelector(
    hasFeatureFlag("updated-mortgagees-response")
  );

  const { isLoading, isError, data } = useQuery<
    boolean,
    boolean,
    MortgageeSearchResult
  >(
    [
      "mortgageeSearch",
      {
        query: searchText,
        limit: 200
      }
    ],
    {
      cacheTime: 0,
      staleTime: Infinity,
      enabled: searchText.length > 0
    }
  );

  const handleChange = (text: string) => {
    setSearchText(text);

    if (text.length === 0) {
      setDropdownOpen(false);
    }
  };

  const debouncedChangeHandler = useMemo(() => debounce(handleChange, 300), []);

  useEffect(() => {
    return () => {
      debouncedChangeHandler.cancel();
    };
  }, []);

  useEffect(() => {
    if (searchText.length) {
      setDropdownOpen(true);
    }
  }, [isLoading]);

  const handleSelectMortgagee = (index: string) => {
    const propertyName = hasUpdatedMortgageesResponse
      ? "Index"
      : "Mortgagee0Index";
    const selectedMortgagee = data?.mortgagees.find(
      mortgagee => mortgagee[propertyName] === index
    );

    if (isQTC) {
      track("Mortgagee Updated", {
        mortgagee: selectedMortgagee,
        inputSource: INPUT_SOURCES.MORTGAGEE_DB,
        inputQuery: searchText
      });
      onChange(selectedMortgagee);
    } else {
      const { AddressLine1, AddressLine2, City, Name, State, ZipCode } =
        GetMortgageeProperties(hasUpdatedMortgageesResponse, selectedMortgagee);

      onChange(
        {
          AddressCity: City,
          AddressLine1: AddressLine1,
          AddressLine2: AddressLine2,
          AddressState: State,
          AddressZip: ZipCode,
          Mortgagee: Name
        },
        searchText
      );
    }

    setDropdownOpen(false);
  };

  const openAddMortgageeModal = () => {
    if (isQTC) {
      dispatch(
        externalResetChildForm({
          id: QTC.QTC_FORM_ID,
          childId: "newMortgagee"
        })
      );

      dispatch(
        toggleMortgageeModal(true, {
          Position: fusionPosition,
          onlyAddMortgagee: true
        })
      );
    } else {
      onEnterMortgagee();
    }
  };

  return (
    <StyledSearchField $isQTC={isQTC} data-bdd="mortgagee-search">
      <SearchField
        onChange={debouncedChangeHandler}
        aria-label="Search Mortgagee"
        data-bdd="mortgagee-search-field"
        onBlur={onBlur}
      />
      <DropdownMenu
        open={dropdownOpen}
        $isQTC={isQTC}
        onClose={() => setDropdownOpen(false)}
        data-bdd="mortgagee-search-dropdown-menu"
      >
        {isLoading && (
          <StyledSpinner data-bdd="mortgagee-search-dropdown-loader">
            <Spinner />
          </StyledSpinner>
        )}
        {data?.mortgagees.slice(0, MORTGAGEES_LIST_LIMIT).map(mortgagee => {
          const { AddressLine1, City, Name, State, ZipCode, Index } =
            GetMortgageeProperties(hasUpdatedMortgageesResponse, mortgagee);

          return (
            <DropdownSuggestion
              key={Index}
              eventKey={Index}
              onSelect={handleSelectMortgagee}
              data-bdd="mortgagee-search-dropdown-suggestion"
            >
              <MortgageeName>
                <img src="../../../assets/svg/mortgagee-icon.svg" />
                {Name}
              </MortgageeName>
              <Span size="sm">
                {AddressLine1} {City}, {State} {ZipCode}
              </Span>
            </DropdownSuggestion>
          );
        })}
        {isError && (
          <SearchError>
            There was an error searching for mortgagees. You can try again, or
            use the link below to add a mortgagee.
          </SearchError>
        )}
        <DropdownAction
          onSelect={openAddMortgageeModal}
          data-bdd="mortgagee-search-dropdown-add"
        >
          <UiIcon name="AddCircle" size="xs" />
          Add my own
        </DropdownAction>
      </DropdownMenu>
    </StyledSearchField>
  );
};

MortgageeSearch.propTypes = {
  onChange: PropTypes.func.isRequired,
  fusionPosition: PropTypes.number,
  onEnterMortgagee: PropTypes.func,
  onBlur: PropTypes.func
};
